/**
 * Email Service
 * Handles email notifications via backend API
 * 
 * Security: AWS credentials are NEVER stored in frontend code.
 * All emails are sent through the backend API endpoint.
 */
import logger from '../utils/logger';
import settingsService from './settingsService';
import { getEnv, getEnvBool } from '../utils/envConfig';

class EmailService {
  constructor() {
    // Email API endpoint (backend handles credentials)
    this.apiEndpoint = getEnv('REACT_APP_EMAIL_API_ENDPOINT', null);
    
    // Check if emails are globally disabled via environment variable
    this.envEmailEnabled = getEnvBool('REACT_APP_EMAIL_NOTIFICATIONS_ENABLED', true);
  }

  /**
   * Check if email notifications are enabled
   * Checks both environment variable and settings
   */
  async isEmailEnabled() {
    try {
      // First check environment variable (takes precedence)
      if (!this.envEmailEnabled) {
        logger.debug('Email notifications disabled via environment variable');
        return false;
      }

      // Then check settings (admin-controlled)
      const settings = await settingsService.getSettings();
      const emailEnabled = settings.emailNotificationsEnabled !== false; // Default to true if not set
      
      if (!emailEnabled) {
        logger.debug('Email notifications disabled via settings');
      }
      
      return emailEnabled;
    } catch (error) {
      // If we can't check settings, default to enabled (fail open)
      logger.warning('Error checking email enabled status, defaulting to enabled', error);
      return this.envEmailEnabled;
    }
  }

  /**
   * Send email via backend API endpoint (only supported mode)
   */
  async sendEmail({ to, subject, htmlBody, textBody }) {
    try {
      logger.info('Attempting to send email', {
        to,
        subject,
        hasApiEndpoint: !!this.apiEndpoint,
        envEmailEnabled: this.envEmailEnabled,
      });
      
      // Check if email notifications are enabled
      const emailEnabled = await this.isEmailEnabled();
      if (!emailEnabled) {
        logger.info('Email notifications are disabled, skipping email send', { to, subject });
        return { success: false, error: 'Email notifications are disabled', skipped: true };
      }

      if (!to) {
        logger.warning('Cannot send email: recipient email not provided');
        return { success: false, error: 'Recipient email required' };
      }

      // Validate API endpoint is configured
      if (!this.apiEndpoint) {
        logger.warning('Email service not configured: REACT_APP_EMAIL_API_ENDPOINT not set', {
          to,
          subject,
        });
        return { success: false, error: 'Email service not configured' };
      }

      // Send via backend API
      return await this.sendEmailViaAPI({ to, subject, htmlBody, textBody });
    } catch (error) {
      logger.error('Error sending email', error, { to, subject });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email via backend API endpoint
   */
  async sendEmailViaAPI({ to, subject, htmlBody, textBody }) {
    try {
      logger.debug('Posting email payload to backend API', {
        endpoint: this.apiEndpoint,
        to,
        subject,
      });
      
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          subject,
          body: textBody,
          html: htmlBody,
        }),
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      logger.info('Email sent via API', { to, subject, result });
      return { success: true, ...result };
    } catch (error) {
      logger.error('Error sending email via API', error, {
        to,
        subject,
        endpoint: this.apiEndpoint,
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Helper to send email with additional context logging
   */
  async sendEmailWithDiagnostics(operation, payload, contextMeta = {}) {
    try {
      const result = await this.sendEmail(payload);
      
      if (result.success) {
        logger.info(`Email operation succeeded: ${operation}`, {
          ...contextMeta,
          to: payload.to,
          subject: payload.subject,
          messageId: result.messageId,
        });
      } else if (result.skipped) {
        logger.info(`Email operation skipped: ${operation}`, {
          ...contextMeta,
          to: payload.to,
          subject: payload.subject,
          reason: result.error,
        });
      } else {
        logger.error(`Email operation failed: ${operation}`, {
          ...contextMeta,
          to: payload.to,
          subject: payload.subject,
          error: result.error,
        });
      }
      
      return result;
    } catch (error) {
      logger.error(`Unexpected error during email operation: ${operation}`, error, contextMeta);
      throw error;
    }
  }

  /**
   * Send email notification for request approval
   */
  async sendRequestApprovalEmail(employee, request) {
    try {
      const subject = 'Your Innovation Request Has Been Approved';
      const htmlBody = this.getRequestApprovalTemplate(employee, request);
      const textBody = this.getRequestApprovalTextTemplate(employee, request);

      return await this.sendEmailWithDiagnostics(
        'requestApproval',
        {
        to: employee.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          employeeId: employee?.id,
          requestId: request?.id,
        }
      );
    } catch (error) {
      logger.error('Error sending request approval email', error, {
        employeeId: employee?.id,
        requestId: request?.id,
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email notification for request rejection
   */
  async sendRequestRejectionEmail(employee, request, rejectionReason = '') {
    try {
      const subject = 'Your Innovation Request Has Been Rejected';
      const htmlBody = this.getRequestRejectionTemplate(employee, request, rejectionReason);
      const textBody = this.getRequestRejectionTextTemplate(employee, request, rejectionReason);

      return await this.sendEmailWithDiagnostics(
        'requestRejection',
        {
        to: employee.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          employeeId: employee?.id,
          requestId: request?.id,
          rejectionReason,
        }
      );
    } catch (error) {
      logger.error('Error sending request rejection email', error, {
        employeeId: employee?.id,
        requestId: request?.id,
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email notification for new request (notify manager)
   */
  async sendNewRequestNotificationEmail(manager, employee, request) {
    try {
      const subject = `New Innovation Request from ${employee.name}`;
      const htmlBody = this.getNewRequestTemplate(manager, employee, request);
      const textBody = this.getNewRequestTextTemplate(manager, employee, request);

      return await this.sendEmailWithDiagnostics(
        'newRequestNotification',
        {
        to: manager.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          managerId: manager?.id,
          employeeId: employee?.id,
          requestId: request?.id,
        }
      );
    } catch (error) {
      logger.error('Error sending new request notification email', error, {
        managerId: manager?.id,
        employeeId: employee?.id,
        requestId: request?.id,
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email notification for cancellation approval
   */
  async sendCancellationApprovalEmail(employee, request) {
    try {
      const subject = 'Your Cancellation Request Has Been Approved';
      const htmlBody = this.getCancellationApprovalTemplate(employee, request);
      const textBody = this.getCancellationApprovalTextTemplate(employee, request);

      return await this.sendEmailWithDiagnostics(
        'cancellationApproval',
        {
        to: employee.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          employeeId: employee?.id,
          requestId: request?.id,
        }
      );
    } catch (error) {
      logger.error('Error sending cancellation approval email', error, {
        employeeId: employee?.id,
        requestId: request?.id,
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email notification for cancellation rejection
   */
  async sendCancellationRejectionEmail(employee, request, reason = '') {
    try {
      const subject = 'Your Cancellation Request Has Been Rejected';
      const htmlBody = this.getCancellationRejectionTemplate(employee, request, reason);
      const textBody = this.getCancellationRejectionTextTemplate(employee, request, reason);

      return await this.sendEmailWithDiagnostics(
        'cancellationRejection',
        {
        to: employee.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          employeeId: employee?.id,
          requestId: request?.id,
          reason,
        }
      );
    } catch (error) {
      logger.error('Error sending cancellation rejection email', error, {
        employeeId: employee?.id,
        requestId: request?.id,
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send welcome email to new employee
   */
  async sendWelcomeEmail(employee, temporaryPassword = null) {
    try {
      const subject = 'Welcome to IDT Ignite Quarks Platform';
      const htmlBody = this.getWelcomeTemplate(employee, temporaryPassword);
      const textBody = this.getWelcomeTextTemplate(employee, temporaryPassword);

      return await this.sendEmailWithDiagnostics(
        'welcomeEmail',
        {
        to: employee.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          employeeId: employee?.id,
          temporaryPasswordProvided: !!temporaryPassword,
        }
      );
    } catch (error) {
      logger.error('Error sending welcome email', error, { employeeId: employee?.id });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(employee) {
    try {
      const subject = 'Your Password Has Been Reset';
      const htmlBody = this.getPasswordResetTemplate(employee);
      const textBody = this.getPasswordResetTextTemplate(employee);

      return await this.sendEmailWithDiagnostics(
        'passwordReset',
        {
        to: employee.email,
        subject,
        htmlBody,
        textBody,
        },
        {
          employeeId: employee?.id,
        }
      );
    } catch (error) {
      logger.error('Error sending password reset email', error, { employeeId: employee?.id });
      return { success: false, error: error.message };
    }
  }

  // Email Templates

  getRequestApprovalTemplate(employee, request) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #003087; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .button { background-color: #003087; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Request Approved</h1>
            </div>
            <div class="content">
              <p>Hello ${employee.name},</p>
              <p>Great news! Your innovation request has been approved.</p>
              <p><strong>Request Details:</strong></p>
              <ul>
                <li><strong>Start Date:</strong> ${startDate}</li>
                <li><strong>End Date:</strong> ${endDate}</li>
                <li><strong>Days:</strong> ${request.days}</li>
                <li><strong>Reason:</strong> ${request.reason || 'N/A'}</li>
              </ul>
              <p>You can view your request details in the platform.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getRequestApprovalTextTemplate(employee, request) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
Hello ${employee.name},

Great news! Your innovation request has been approved.

Request Details:
- Start Date: ${startDate}
- End Date: ${endDate}
- Days: ${request.days}
- Reason: ${request.reason || 'N/A'}

You can view your request details in the platform.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  getRequestRejectionTemplate(employee, request, rejectionReason) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #d32f2f; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Request Rejected</h1>
            </div>
            <div class="content">
              <p>Hello ${employee.name},</p>
              <p>We regret to inform you that your innovation request has been rejected.</p>
              <p><strong>Request Details:</strong></p>
              <ul>
                <li><strong>Start Date:</strong> ${startDate}</li>
                <li><strong>End Date:</strong> ${endDate}</li>
                <li><strong>Days:</strong> ${request.days}</li>
                <li><strong>Reason:</strong> ${request.reason || 'N/A'}</li>
              </ul>
              ${rejectionReason ? `<p><strong>Rejection Reason:</strong> ${rejectionReason}</p>` : ''}
              <p>If you have any questions, please contact your manager.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getRequestRejectionTextTemplate(employee, request, rejectionReason) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
Hello ${employee.name},

We regret to inform you that your innovation request has been rejected.

Request Details:
- Start Date: ${startDate}
- End Date: ${endDate}
- Days: ${request.days}
- Reason: ${request.reason || 'N/A'}

${rejectionReason ? `Rejection Reason: ${rejectionReason}\n` : ''}
If you have any questions, please contact your manager.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  getNewRequestTemplate(manager, employee, request) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #003087; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .button { background-color: #003087; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>New Innovation Request</h1>
            </div>
            <div class="content">
              <p>Hello ${manager.name},</p>
              <p>You have a new innovation request from ${employee.name} that requires your approval.</p>
              <p><strong>Request Details:</strong></p>
              <ul>
                <li><strong>Employee:</strong> ${employee.name} (${employee.email})</li>
                <li><strong>Start Date:</strong> ${startDate}</li>
                <li><strong>End Date:</strong> ${endDate}</li>
                <li><strong>Days:</strong> ${request.days}</li>
                <li><strong>Reason:</strong> ${request.reason || 'N/A'}</li>
              </ul>
              <p>Please log in to the platform to review and approve or reject this request.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getNewRequestTextTemplate(manager, employee, request) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
Hello ${manager.name},

You have a new innovation request from ${employee.name} that requires your approval.

Request Details:
- Employee: ${employee.name} (${employee.email})
- Start Date: ${startDate}
- End Date: ${endDate}
- Days: ${request.days}
- Reason: ${request.reason || 'N/A'}

Please log in to the platform to review and approve or reject this request.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  getCancellationApprovalTemplate(employee, request) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #003087; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Cancellation Approved</h1>
            </div>
            <div class="content">
              <p>Hello ${employee.name},</p>
              <p>Your cancellation request has been approved. The tokens for this request have been freed.</p>
              <p><strong>Original Request Details:</strong></p>
              <ul>
                <li><strong>Start Date:</strong> ${startDate}</li>
                <li><strong>End Date:</strong> ${endDate}</li>
                <li><strong>Days:</strong> ${request.days}</li>
              </ul>
              <p>You can view your updated token balance in the platform.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getCancellationApprovalTextTemplate(employee, request) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
Hello ${employee.name},

Your cancellation request has been approved. The tokens for this request have been freed.

Original Request Details:
- Start Date: ${startDate}
- End Date: ${endDate}
- Days: ${request.days}

You can view your updated token balance in the platform.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  getCancellationRejectionTemplate(employee, request, reason) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #d32f2f; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Cancellation Rejected</h1>
            </div>
            <div class="content">
              <p>Hello ${employee.name},</p>
              <p>Your cancellation request has been rejected. Your original request remains active.</p>
              <p><strong>Request Details:</strong></p>
              <ul>
                <li><strong>Start Date:</strong> ${startDate}</li>
                <li><strong>End Date:</strong> ${endDate}</li>
                <li><strong>Days:</strong> ${request.days}</li>
              </ul>
              ${reason ? `<p><strong>Reason:</strong> ${reason}</p>` : ''}
              <p>If you have any questions, please contact your manager.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getCancellationRejectionTextTemplate(employee, request, reason) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
Hello ${employee.name},

Your cancellation request has been rejected. Your original request remains active.

Request Details:
- Start Date: ${startDate}
- End Date: ${endDate}
- Days: ${request.days}

${reason ? `Reason: ${reason}\n` : ''}
If you have any questions, please contact your manager.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  getWelcomeTemplate(employee, temporaryPassword) {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #003087; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .credentials { background-color: #fff; padding: 15px; border-left: 4px solid #003087; margin: 15px 0; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Welcome to IDT Ignite Quarks Platform</h1>
            </div>
            <div class="content">
              <p>Hello ${employee.name},</p>
              <p>Welcome to the IDT Ignite Quarks Platform! Your account has been created.</p>
              <p><strong>Your Account Details:</strong></p>
              <div class="credentials">
                <p><strong>Email:</strong> ${employee.email}</p>
                <p><strong>Role:</strong> ${employee.role}</p>
                ${temporaryPassword ? `<p><strong>Temporary Password:</strong> ${temporaryPassword}</p>
                <p><em>Please change your password after your first login.</em></p>` : '<p>Please use the password you set during account creation.</p>'}
              </div>
              <p>You can now log in to the platform to request time for innovation projects.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getWelcomeTextTemplate(employee, temporaryPassword) {
    return `
Hello ${employee.name},

Welcome to the IDT Ignite Quarks Platform! Your account has been created.

Your Account Details:
- Email: ${employee.email}
- Role: ${employee.role}
${temporaryPassword ? `- Temporary Password: ${temporaryPassword}\n\nPlease change your password after your first login.` : '\nPlease use the password you set during account creation.'}

You can now log in to the platform to request time for innovation projects.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  getPasswordResetTemplate(employee) {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #003087; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .warning { background-color: #fff3cd; padding: 15px; border-left: 4px solid #ffc107; margin: 15px 0; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Password Reset</h1>
            </div>
            <div class="content">
              <p>Hello ${employee.name},</p>
              <p>Your password has been reset by an administrator.</p>
              <div class="warning">
                <p><strong>Important:</strong> Please contact your administrator to obtain your new password, or use the password reset feature in the platform.</p>
              </div>
              <p>If you did not request this password reset, please contact your administrator immediately.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getPasswordResetTextTemplate(employee) {
    return `
Hello ${employee.name},

Your password has been reset by an administrator.

Important: Please contact your administrator to obtain your new password, or use the password reset feature in the platform.

If you did not request this password reset, please contact your administrator immediately.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }

  /**
   * Send email notification for cancellation request (notify manager)
   */
  async sendCancellationRequestNotificationEmail(manager, employee, request, cancellationReason = '') {
    try {
      const subject = `Cancellation Request from ${employee.name}`;
      const htmlBody = this.getCancellationRequestTemplate(manager, employee, request, cancellationReason);
      const textBody = this.getCancellationRequestTextTemplate(manager, employee, request, cancellationReason);

      return await this.sendEmailWithDiagnostics(
        'cancellationRequestNotification',
        {
          to: manager.email,
          subject,
          htmlBody,
          textBody,
        },
        {
          managerId: manager?.id,
          employeeId: employee?.id,
          requestId: request?.id,
          cancellationReason,
        }
      );
    } catch (error) {
      logger.error('Error sending cancellation request notification email', error, {
        managerId: manager?.id,
        employeeId: employee?.id,
        requestId: request?.id,
      });
      return { success: false, error: error.message };
    }
  }

  getCancellationRequestTemplate(manager, employee, request, cancellationReason) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #ff9800; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .button { background-color: #003087; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px; }
            .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
            .reason-box { background-color: #fff3cd; padding: 15px; border-left: 4px solid #ff9800; margin: 15px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Cancellation Request</h1>
            </div>
            <div class="content">
              <p>Hello ${manager.name},</p>
              <p>${employee.name} has requested to cancel an approved innovation request that requires your approval.</p>
              <p><strong>Request Details:</strong></p>
              <ul>
                <li><strong>Employee:</strong> ${employee.name} (${employee.email})</li>
                <li><strong>Start Date:</strong> ${startDate}</li>
                <li><strong>End Date:</strong> ${endDate}</li>
                <li><strong>Days:</strong> ${request.days}</li>
                <li><strong>Original Status:</strong> ${request.originalStatus || 'N/A'}</li>
              </ul>
              ${cancellationReason ? `<div class="reason-box">
                <strong>Cancellation Reason:</strong> ${cancellationReason}
              </div>` : ''}
              <p>Please log in to the platform to review and approve or reject this cancellation request.</p>
              <p>Best regards,<br>IDT Ignite Quarks Platform</p>
            </div>
            <div class="footer">
              <p>This is an automated notification. Please do not reply to this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  }

  getCancellationRequestTextTemplate(manager, employee, request, cancellationReason) {
    const startDate = new Date(request.startDate).toLocaleDateString();
    const endDate = new Date(request.endDate).toLocaleDateString();
    return `
Hello ${manager.name},

${employee.name} has requested to cancel an approved innovation request that requires your approval.

Request Details:
- Employee: ${employee.name} (${employee.email})
- Start Date: ${startDate}
- End Date: ${endDate}
- Days: ${request.days}
- Original Status: ${request.originalStatus || 'N/A'}

${cancellationReason ? `Cancellation Reason: ${cancellationReason}\n` : ''}
Please log in to the platform to review and approve or reject this cancellation request.

Best regards,
IDT Ignite Quarks Platform
    `.trim();
  }
}

const emailService = new EmailService();
export default emailService;

